import { useCallback, useMemo, useState } from 'react';

import {
  UserCauseSelection,
  useDeleteUserCauseSelection,
  useListUserCauseSelections,
} from 'core/Api';
import { ClickableOptionsCard } from 'core/Components';
import { getErrorMessage_v2 } from 'core/Utils';
import { Flex, ImageMantine, LoadingOverlay, Stack, Text, ThemeIcon, notifications } from 'ui';
import { IconDelete } from 'ui/Icons';

import classes from './CausesFollowing.module.css';
import { UnfollowConfirmationModal } from './UnfollowConfirmationModal';

const DeleteCauseButton = ({
  causeSelection,
  handleRemoveCause,
}: {
  causeSelection: UserCauseSelection;
  handleRemoveCause: (causeSelection: UserCauseSelection) => void;
}) => {
  return (
    <Flex w="100%" justify="flex-end">
      <ThemeIcon
        variant="transparent"
        color="cerise.7"
        onClick={(e) => {
          e.stopPropagation();
          handleRemoveCause(causeSelection);
        }}
      >
        <IconDelete size={18} />
      </ThemeIcon>
    </Flex>
  );
};

const YourFollowing = () => {
  const { data: userCauseSelections, isFetching: isLoadingUserCauseSelections } =
    useListUserCauseSelections();

  const { mutateAsync: deleteCauseSelection, isPending: isDeletingCauseSelection } =
    useDeleteUserCauseSelection();

  const [showUnfollowModal, setShowUnfollowModal] = useState(false);
  const [causeToUnfollow, setCauseToUnfollow] = useState<UserCauseSelection | null>(null);

  const proceedWithDeletion = useCallback(
    (causeSelection: UserCauseSelection) => {
      const causeUuid = causeSelection.cause?.uuid;
      void deleteCauseSelection(
        { causeId: causeUuid },
        {
          onSuccess: () => {
            notifications.show({
              message: 'Cause unfollowed successfully',
              variant: 'success',
            });
          },
          onError: (error) => {
            notifications.show({
              message: getErrorMessage_v2(error),
              variant: 'error',
            });
          },
        }
      );
    },
    [deleteCauseSelection]
  );

  const handleRemoveCause = useCallback(
    (causeSelection: UserCauseSelection) => {
      if (!userCauseSelections) return;
      // Check if this is the last cause
      if (userCauseSelections.length === 1) {
        setCauseToUnfollow(causeSelection);
        setShowUnfollowModal(true);
        return;
      }
      // For non-last causes, proceed directly
      proceedWithDeletion(causeSelection);
    },
    [userCauseSelections, proceedWithDeletion]
  );

  const handleModalActions = useCallback(
    (confirmDeletion: boolean) => {
      if (confirmDeletion && causeToUnfollow) {
        proceedWithDeletion(causeToUnfollow);
      }
      setShowUnfollowModal(false);
      setCauseToUnfollow(null);
    },
    [proceedWithDeletion, causeToUnfollow]
  );

  const causeList = useMemo(() => {
    return (
      <ClickableOptionsCard bg="gray.0" bd="1px solid gray.0">
        {userCauseSelections?.map((causeSelection) => {
          const cause = causeSelection?.cause;
          if (!cause) return null;
          return (
            <ClickableOptionsCard.Row
              key={cause.uuid}
              groupProps={{ grow: true }}
              leftSection={
                <ImageMantine src={cause.logo?.url} radius={12} className={classes.thumbnail} />
              }
              title={cause.name}
              rightSection={
                <DeleteCauseButton
                  causeSelection={causeSelection}
                  handleRemoveCause={handleRemoveCause}
                />
              }
            />
          );
        })}
      </ClickableOptionsCard>
    );
  }, [userCauseSelections, handleRemoveCause]);

  if (isLoadingUserCauseSelections) {
    return <LoadingOverlay visible={true} />;
  }

  return (
    <Stack>
      <Text fz="18px" fw={600}>
        You&apos;re Following
      </Text>
      {causeList}
      <UnfollowConfirmationModal
        opened={showUnfollowModal}
        onClose={() => handleModalActions(false)}
        handleModalActions={handleModalActions}
        causeName={causeToUnfollow?.cause?.name}
        isUpdatingCauseSelections={isDeletingCauseSelection}
      />
    </Stack>
  );
};

export { YourFollowing };
